/**
 * Config-driven registry for the public-ArcGIS category renderer (#213).
 *
 * ONE generalized layer component (ArcgisFeatureLayer) renders every
 * arcgis_public_features category off this table — adding a category later is a
 * config ENTRY here, not a new component (mirrors the ingest-side config-driven
 * FEEDS[]). Each entry binds a DB `category` to its COP layer key, the LayerPrefs
 * boolean that toggles it, its context-locked palette resolver, the side-panel
 * accent/label, and its audit verbs.
 *
 * The evacuation category (the original bespoke #204 layer) is included here so it
 * renders through the SAME generalized path; it keeps its own #204 audit verbs +
 * detail panel. Every other category shares the generic verbs (migration 0065) and
 * the generic detail panel, with the category travelling in the audit payload.
 */
import type {
  ArcgisFeatureProperties,
  LayerKey,
  OperatorActionType,
} from '@tsr/shared-types';
import type { CahsGlyphKey } from '../assets/cahs/index.generated';
import type { LayerPrefs } from '../prefs';
import type { ArcgisLayerStyle } from './arcgisStyle';
import { evacuationStyle } from '../styles/evacuationStyles';
import { fireRestrictionStyle } from '../styles/fireRestrictionStyles';
import { droughtStyle } from '../styles/droughtStyles';
import { publicHealthStyle } from '../styles/publicHealthStyles';
import { emergencyDeclarationStyle } from '../styles/emergencyDeclarationStyles';
import { tropicalCycloneStyle } from '../styles/tropicalCycloneStyles';

/** The boolean fields of LayerPrefs (a category toggle is always a boolean). */
type BooleanPrefKey = {
  [K in keyof LayerPrefs]: LayerPrefs[K] extends boolean ? K : never;
}[keyof LayerPrefs];

export interface ArcgisCategoryConfig {
  /** arcgis_public_features.category — the `?category=` API filter + render key. */
  category: string;
  /** Stable server layer_key for the COP toggle + persistence. */
  layerKey: LayerKey;
  /** The LayerPrefs boolean this category's toggle drives. */
  prefKey: BooleanPrefKey;
  /** Operator-facing layer label (panel heading + register noun). */
  label: string;
  /** Group the layer is homed in (for the detail-panel breadcrumb). */
  groupLabel: string;
  /** Representative accent colour for the side-panel header (a family primary). */
  accentColor: string;
  /** Geometry-agnostic render style resolved from the feature's severity register. */
  style: (props: ArcgisFeatureProperties) => ArcgisLayerStyle;
  /** Audit verb when the operator clicks a feature in this layer. */
  auditClickVerb: OperatorActionType;
  /** Audit verb when this category's detail panel opens. */
  auditDetailVerb: OperatorActionType;
  /** True → route the side panel to the bespoke EvacuationOrderBody (population /
   *  homes cards) instead of the generic ArcgisFeatureBody. */
  useEvacuationBody?: boolean;
  /** Icon one-swing #564: a CAHS glyph rendered as a centroid EXACT-ACT DISC on this
   *  category's POLYGON features (a solid category-coloured disc + white glyph knocked
   *  out). Fixes the evac-order missing-glyph gap — an evac polygon rendered NO symbol.
   *  Undefined → the ambient area/outline only (unchanged for every other category). */
  centroidGlyph?: CahsGlyphKey;
}

/** Adapt the evacuation family's own style shape onto the shared ArcgisLayerStyle. */
function evacStyle(props: ArcgisFeatureProperties): ArcgisLayerStyle {
  const s = evacuationStyle(props.severity);
  return {
    color: s.color,
    fillAlpha: s.alpha,
    strokeAlpha: s.strokeAlpha,
    outlineWidth: s.outlineWidth,
    pointPixelSize: 12,
    label: s.label,
  };
}

export const ARCGIS_CATEGORIES: readonly ArcgisCategoryConfig[] = [
  {
    category: 'evacuation',
    layerKey: 'bc-evacuation-orders',
    prefKey: 'evacuationOrders',
    label: 'Evacuation Orders & Alerts',
    groupLabel: 'Hazard',
    accentColor: '#6d28d9',
    style: evacStyle,
    auditClickVerb: 'click_evacuation_order',
    auditDetailVerb: 'view_evacuation_order_detail',
    useEvacuationBody: true,
    // Icon one-swing #564: an evac polygon showed NO symbol — render the EXACT `evacuation`
    // disc glyph at the order centroid (the same missing-glyph fix as the operator + CAP evac paths).
    centroidGlyph: 'evacuation',
  },
  {
    category: 'fire_restriction',
    layerKey: 'ab-fire-bans',
    prefKey: 'fireBans',
    label: 'Fire Bans & Restrictions',
    groupLabel: 'Fire Risk',
    accentColor: '#9a3412',
    style: (p) => fireRestrictionStyle(p.severity),
    auditClickVerb: 'click_arcgis_feature',
    auditDetailVerb: 'view_arcgis_feature_detail',
  },
  {
    category: 'drought',
    layerKey: 'drought-conditions',
    prefKey: 'droughtConditions',
    label: 'Drought Conditions',
    groupLabel: 'Advisories & Declarations',
    accentColor: '#a8843f',
    style: (p) => droughtStyle(p.severity),
    auditClickVerb: 'click_arcgis_feature',
    auditDetailVerb: 'view_arcgis_feature_detail',
  },
  {
    category: 'public_health',
    layerKey: 'boil-water-advisories',
    prefKey: 'boilWaterAdvisories',
    label: 'Boil Water Advisories',
    groupLabel: 'Advisories & Declarations',
    accentColor: '#0e7490',
    style: (p) => publicHealthStyle(p.severity),
    auditClickVerb: 'click_arcgis_feature',
    auditDetailVerb: 'view_arcgis_feature_detail',
  },
  {
    category: 'emergency_declaration',
    layerKey: 'bc-state-of-local-emergency',
    prefKey: 'stateOfLocalEmergency',
    label: 'State of Local Emergency',
    groupLabel: 'Advisories & Declarations',
    accentColor: '#a21caf',
    style: (p) => emergencyDeclarationStyle(p.severity),
    auditClickVerb: 'click_arcgis_feature',
    auditDetailVerb: 'view_arcgis_feature_detail',
  },
  {
    category: 'hurricane',
    layerKey: 'tropical-cyclones',
    prefKey: 'tropicalCyclones',
    label: 'Tropical Cyclones',
    groupLabel: 'Advisories & Declarations',
    accentColor: '#0369a1',
    style: (p) => tropicalCycloneStyle(p.severity),
    auditClickVerb: 'click_arcgis_feature',
    auditDetailVerb: 'view_arcgis_feature_detail',
  },
];

const BY_CATEGORY = new Map<string, ArcgisCategoryConfig>(
  ARCGIS_CATEGORIES.map((c) => [c.category, c]),
);

/** Resolve the category config for an arcgis feature's category (null if unknown). */
export function arcgisConfigForCategory(category: string): ArcgisCategoryConfig | null {
  return BY_CATEGORY.get(category) ?? null;
}

/** Project the layer prefs into the per-category visibility map MapView consumes. */
export function arcgisVisibilityFromPrefs(prefs: LayerPrefs): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  for (const c of ARCGIS_CATEGORIES) out[c.category] = Boolean(prefs[c.prefKey]);
  return out;
}
